import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import warrantyApi from "@/api/warrantyApi";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PageHeader,
  PageHeaderHeading,
} from "@/components/ui/page-header";
import { PageContainer } from "@/components/layout/PageContainer";
import { all_routes } from "../../Router/all_routes";
import {
  ArrowLeft,
  Save,
  Search,
  Receipt,
  Package,
  CheckCircle2,
  RotateCcw,
  Loader2,
} from "lucide-react";
import { ReactSelect } from "@/components/ui/react-select";
import {
  getSaleItemReturnDetails,
  getItemSerials,
  WARRANTY_RETURN_CONDITIONS,
  calculateWarrantyDates,
} from "@/lib/warrantyUtils";
import { cn, formatDate } from "@/lib/utils";

const AddWarrantyClaim = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const currentStore = useSelector((state) => state.current_store);

  // Permission check
  const rawPermissions = useSelector((state) => state.userpermission_data);
  const permissions = useMemo(() => rawPermissions || [], [rawPermissions]);
  const hasPermission = (perm) => permissions.includes(perm);
  const canCreateClaim = hasPermission("warranty claim create");

  // Form mode: Prefer sale search (true) or manual entry without sale (false)
  const [useSaleLookup, setUseSaleLookup] = useState(true);

  // Sale Search State
  const [saleSearchQuery, setSaleSearchQuery] = useState("");
  const [debouncedSaleQuery, setDebouncedSaleQuery] = useState("");
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [selectedSale, setSelectedSale] = useState(() => location.state?.sale || null);
  const [selectedSaleItem, setSelectedSaleItem] = useState(
    () => location.state?.item || null,
  );
  const [loadedProduct, setLoadedProduct] = useState(
    () => location.state?.item?.product || location.state?.item?.product_data || null,
  );

  // Click outside listener for sale search dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setIsSearchDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Main Form Data
  const [formData, setFormData] = useState(() => {
    let initialState = {
      customer_id: "",
      product_id: "",
      store_id: currentStore?.id || "",
      sale_id: "",
      sale_item_id: "",
      serial_number: "",
      item_description: "",
      defect_description: "",
      return_condition: "defective",
      warranty_start_date: "",
      warranty_end_date: "",
      sale_invoice_number: "",
      customer_name: "",
    };

    if (location.state?.sale && location.state?.item) {
      const { sale, item } = location.state;
      const serials = getItemSerials(item);
      const itemReturnDetails = getSaleItemReturnDetails(item);
      let serial =
        serials[0] ||
        itemReturnDetails.serialNumber ||
        item.inventory_serial?.serial_number ||
        item.serial_number ||
        "";
      if (!serial && item.serial_numbers) {
        try {
          const parsed = JSON.parse(item.serial_numbers);
          if (Array.isArray(parsed) && parsed.length > 0) serial = parsed[0];
        } catch {
          serial = item.serial_numbers;
        }
      }

      // Automatically determine start date from item > sale date > item created_at > today
      const rawStart =
        item.warranty_start_date ||
        sale.created_at ||
        item.created_at ||
        new Date().toISOString();
      const startDate = rawStart
        ? rawStart.split("T")[0]
        : new Date().toISOString().split("T")[0];

      // Automatically determine end date: if already set on item use it, else calculate from product
      let endDate = item.warranty_end_date
        ? item.warranty_end_date.split("T")[0]
        : "";
      if (!endDate) {
        const prodCandidate = item.product || item.product_data;
        const calculated = calculateWarrantyDates(prodCandidate, startDate);
        endDate = calculated.endDate;
      }

      initialState = {
        ...initialState,
        sale_id: sale.id,
        sale_invoice_number: sale.invoice_number,
        customer_id: sale.customer_id || sale.customer?.id || "",
        customer_name:
          sale.customer?.name ||
          sale.customer?.customer_name ||
          sale.customer?.full_name ||
          "Customer",
        store_id: sale.store_id || initialState.store_id,
        sale_item_id: item.id,
        product_id: item.product_id || item.product?.id || "",
        item_description:
          item.product?.name || item.product_data?.name || "Selected Product",
        serial_number: serial,
        warranty_start_date: startDate,
        warranty_end_date: endDate,
      };
    }
    return initialState;
  });

  // Debounce sale search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSaleQuery(saleSearchQuery.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [saleSearchQuery]);

  // Query Sales by invoice or customer
  const { data: searchedSales = [], isFetching: isSearchingSales } = useQuery({
    queryKey: ["sales-search-warranty", debouncedSaleQuery, currentStore?.id],
    queryFn: async () => {
      if (!debouncedSaleQuery) return [];
      const res = await apiClient.get(API_ENDPOINTS.SALES.SALES, {
        params: {
          search: debouncedSaleQuery,
          store_id: currentStore?.id,
          per_page: 10,
        },
      });
      return res.data?.data || res.data || [];
    },
    enabled: useSaleLookup && debouncedSaleQuery.length > 0,
  });

  // Search input debounces for customers, products, stores
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setCustomerSearchTerm(customerSearchInput), 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  const [productSearchInput, setProductSearchInput] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setProductSearchTerm(productSearchInput), 300);
    return () => clearTimeout(timer);
  }, [productSearchInput]);

  const [storeSearchInput, setStoreSearchInput] = useState("");
  const [storeSearchTerm, setStoreSearchTerm] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setStoreSearchTerm(storeSearchInput), 300);
    return () => clearTimeout(timer);
  }, [storeSearchInput]);

  // Fetch customers
  const { data: customers = [], isFetching: isCustomersFetching } = useQuery({
    queryKey: ["customers-select", customerSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.SALES.CUSTOMERS, {
        params: { q: customerSearchTerm || undefined, per_page: 50 },
      });
      return res.data.data || res.data || [];
    },
    enabled: !useSaleLookup || !formData.customer_id,
  });

  // Fetch products (keep enabled so product warranty info is accessible)
  const { data: products = [], isFetching: isProductsFetching } = useQuery({
    queryKey: ["products-select", productSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.PRODUCTS, {
        params: { search: productSearchTerm || undefined, per_page: 100 },
      });
      return res.data.data || res.data || [];
    },
  });

  // Fetch stores
  const { data: stores = [], isFetching: isStoresFetching } = useQuery({
    queryKey: ["stores-select", storeSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES, {
        params: { q: storeSearchTerm || undefined },
      });
      return res.data.data || res.data || [];
    },
  });

  // On mount with linked item from navigation: fetch full product details to ensure warranty duration accuracy
  useEffect(() => {
    const prodId = location.state?.item?.product_id;
    if (prodId) {
      apiClient
        .get(API_ENDPOINTS.INVENTORY.PRODUCT_DETAILS(prodId))
        .then((res) => {
          const fullProd = res.data?.data || res.data;
          if (fullProd) {
            setLoadedProduct(fullProd);
            setFormData((prev) => {
              if (!prev.warranty_end_date) {
                const dates = calculateWarrantyDates(fullProd, prev.warranty_start_date);
                return {
                  ...prev,
                  warranty_start_date: prev.warranty_start_date || dates.startDate,
                  warranty_end_date: dates.endDate,
                };
              }
              return prev;
            });
          }
        })
        .catch(() => { });
    }
  }, [location.state?.item?.product_id]);

  // When a sale is selected from the lookup
  const handleSelectSale = (sale) => {
    setSelectedSale(sale);
    setSelectedSaleItem(null);
    setLoadedProduct(null);
    setIsSearchDropdownOpen(false);
    setSaleSearchQuery("");

    const customer = sale.customer;
    const custName =
      customer?.customer_name || customer?.full_name || customer?.name || "Customer";

    setFormData((prev) => ({
      ...prev,
      sale_id: sale.id,
      sale_invoice_number: sale.invoice_number,
      customer_id: sale.customer_id || customer?.id || "",
      customer_name: custName,
      store_id: sale.store_id || prev.store_id || currentStore?.id || "",
      sale_item_id: "",
      product_id: "",
      item_description: "",
      serial_number: "",
      warranty_start_date: "",
      warranty_end_date: "",
    }));
  };

  // Clear selected sale and prepare for fresh search
  const handleClearSale = () => {
    setSelectedSale(null);
    setSelectedSaleItem(null);
    setLoadedProduct(null);
    setSaleSearchQuery("");
    setDebouncedSaleQuery("");
    setIsSearchDropdownOpen(false);

    setFormData((prev) => ({
      ...prev,
      sale_id: "",
      sale_invoice_number: "",
      customer_id: "",
      customer_name: "",
      store_id: currentStore?.id || "",
      sale_item_id: "",
      product_id: "",
      item_description: "",
      serial_number: "",
      warranty_start_date: "",
      warranty_end_date: "",
    }));

    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 60);
  };

  // When a specific sale item is clicked
  const handleSelectSaleItem = (item) => {
    const returnDetails = getSaleItemReturnDetails(item);
    if (returnDetails.isReturned) {
      toast.error(
        `Cannot claim this item: ${returnDetails.reason || "Item already claimed or returned"}`,
      );
      return;
    }

    setSelectedSaleItem(item);

    const serials = getItemSerials(item);
    const serial =
      serials[0] ||
      returnDetails.serialNumber ||
      item.inventory_serial?.serial_number ||
      item.serial_number ||
      "";

    // Determine warranty start date (from sale item > sale creation > item creation > today)
    const rawStart =
      item.warranty_start_date ||
      selectedSale?.created_at ||
      item.created_at ||
      new Date().toISOString();
    const startDate = rawStart
      ? rawStart.split("T")[0]
      : new Date().toISOString().split("T")[0];

    const prodCandidate =
      item.product ||
      item.product_data ||
      products.find((p) => p.id === (item.product_id || item.product?.id));

    // Determine warranty end date:
    // If sale item already has warranty_end_date, use it;
    // Otherwise calculate automatically from product warranty coverage
    let endDate = item.warranty_end_date
      ? item.warranty_end_date.split("T")[0]
      : "";

    if (!endDate && prodCandidate) {
      const dates = calculateWarrantyDates(prodCandidate, startDate);
      endDate = dates.endDate;
    }

    setFormData((prev) => ({
      ...prev,
      sale_item_id: item.id,
      product_id: item.product_id || item.product?.id || "",
      item_description:
        item.product?.name || item.product_data?.name || prev.item_description || "",
      serial_number: serial,
      warranty_start_date: startDate,
      warranty_end_date: endDate,
    }));

    // If product candidate lacks explicit warranty fields, fetch full product details to ensure accurate coverage
    const prodId = item.product_id || item.product?.id;
    if (prodId) {
      apiClient
        .get(API_ENDPOINTS.INVENTORY.PRODUCT_DETAILS(prodId))
        .then((res) => {
          const fullProd = res.data?.data || res.data;
          if (fullProd) {
            setLoadedProduct(fullProd);
            // If end date was not already set on the sale item, calculate from full product
            if (!item.warranty_end_date) {
              const dates = calculateWarrantyDates(fullProd, startDate);
              setFormData((prev) => ({
                ...prev,
                warranty_start_date: prev.warranty_start_date || dates.startDate,
                warranty_end_date: dates.endDate,
              }));
            }
          }
        })
        .catch(() => { });
    } else if (prodCandidate) {
      setLoadedProduct(prodCandidate);
    }
  };

  // When selecting product in manual mode
  const handleProductSelect = (opt) => {
    if (!opt) {
      setFormData((prev) => ({
        ...prev,
        product_id: "",
        item_description: "",
      }));
      setLoadedProduct(null);
      return;
    }

    const prod = products.find((p) => p.id === opt.value);
    const baseStart =
      formData.warranty_start_date || new Date().toISOString().split("T")[0];

    if (prod) {
      setLoadedProduct(prod);
      const dates = calculateWarrantyDates(prod, baseStart);
      setFormData((prev) => ({
        ...prev,
        product_id: opt.value,
        item_description: prod.name,
        warranty_start_date: dates.startDate,
        warranty_end_date: dates.endDate,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        product_id: opt.value,
      }));
      apiClient
        .get(API_ENDPOINTS.INVENTORY.PRODUCT_DETAILS(opt.value))
        .then((res) => {
          const fullProd = res.data?.data || res.data;
          if (fullProd) {
            setLoadedProduct(fullProd);
            const dates = calculateWarrantyDates(fullProd, baseStart);
            setFormData((prev) => ({
              ...prev,
              item_description: fullProd.name,
              warranty_start_date: dates.startDate,
              warranty_end_date: dates.endDate,
            }));
          }
        })
        .catch(() => { });
    }
  };

  // Facility to change warranty start date:
  // When user edits start date, automatically adjust end date based on product duration
  const handleStartDateChange = (e) => {
    const newStart = e.target.value;
    const activeProd =
      loadedProduct ||
      selectedSaleItem?.product ||
      selectedSaleItem?.product_data ||
      products.find((p) => p.id === formData.product_id);

    if (newStart) {
      const dates = calculateWarrantyDates(activeProd, newStart);
      setFormData((prev) => ({
        ...prev,
        warranty_start_date: newStart,
        warranty_end_date: dates.endDate,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        warranty_start_date: newStart,
      }));
    }
  };

  // Facility to change warranty end date:
  // User can freely edit or override the end date
  const handleEndDateChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      warranty_end_date: e.target.value,
    }));
  };

  // Quick reset to product default coverage
  const handleResetWarrantyDates = () => {
    const activeProd =
      loadedProduct ||
      selectedSaleItem?.product ||
      selectedSaleItem?.product_data ||
      products.find((p) => p.id === formData.product_id);

    const baseStart =
      selectedSale?.created_at?.split("T")[0] ||
      selectedSaleItem?.warranty_start_date?.split("T")[0] ||
      selectedSaleItem?.created_at?.split("T")[0] ||
      new Date().toISOString().split("T")[0];

    const dates = calculateWarrantyDates(activeProd, baseStart);
    setFormData((prev) => ({
      ...prev,
      warranty_start_date: dates.startDate,
      warranty_end_date: dates.endDate,
    }));
    toast.info(`Warranty dates set to product default (${dates.label})`);
  };

  // Active product coverage info
  const productCoverage = useMemo(() => {
    const activeProd =
      loadedProduct ||
      selectedSaleItem?.product ||
      selectedSaleItem?.product_data ||
      products.find((p) => p.id === formData.product_id);
    if (!activeProd) return null;
    return calculateWarrantyDates(activeProd, formData.warranty_start_date || new Date());
  }, [
    loadedProduct,
    selectedSaleItem,
    products,
    formData.product_id,
    formData.warranty_start_date,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const saleItems = useMemo(() => {
    if (!selectedSale) return [];
    return (
      selectedSale.items ||
      selectedSale.sale_items ||
      selectedSale.details ||
      []
    );
  }, [selectedSale]);

  const activeReturnDetails = useMemo(() => {
    if (selectedSaleItem) {
      return getSaleItemReturnDetails(selectedSaleItem);
    }
    return null;
  }, [selectedSaleItem]);

  const isItemAutoFilled = Boolean(selectedSaleItem);

  const createClaimMutation = useMutation({
    mutationFn: async (data) => {
      const payload = {
        customer_id: data.customer_id,
        product_id: data.product_id,
        store_id: data.store_id || currentStore?.id,
        sale_id: data.sale_id || null,
        sale_item_id: data.sale_item_id || null,
        serial_number: data.serial_number?.trim() || null,
        item_description: data.item_description?.trim(),
        defect_description: data.defect_description?.trim(),
        return_condition: data.return_condition || "defective",
        warranty_start_date: data.warranty_start_date || null,
        warranty_end_date: data.warranty_end_date || null,
      };

      const res = await warrantyApi.create(payload);
      return res.data;
    },
    onSuccess: (data) => {
      const createdClaim = data?.data || data;
      toast.success(
        `Warranty claim #${createdClaim?.claim_number || "NEW"} registered successfully!`,
      );
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });

      // Per Step 1 UI flow: Redirect to claim detail, show claim number and offer print + sign
      if (createdClaim?.id) {
        navigate(`/warranty-claims/${createdClaim.id}`, {
          state: { isNewClaim: true },
        });
      } else {
        navigate(all_routes.warrantyClaims);
      }
    },
    onError: (error) => {
      const msg = error.response?.data?.message || "Failed to create warranty claim";
      toast.error(msg);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!canCreateClaim) {
      toast.error("You do not have permission ('warranty claim create') to register warranty claims");
      return;
    }

    if (activeReturnDetails?.isReturned) {
      toast.error(
        activeReturnDetails.reason ||
        "Cannot submit claim: this item has already been returned or claimed under warranty.",
      );
      return;
    }

    if (
      !formData.customer_id ||
      !formData.product_id ||
      !formData.item_description?.trim() ||
      !formData.defect_description?.trim()
    ) {
      toast.error("Please fill in all required fields (Customer, Product, Description, Defect).");
      return;
    }

    createClaimMutation.mutate(formData);
  };

  return (
    <PageContainer>
      <PageHeader>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <PageHeaderHeading
            title="New Warranty Claim (Intake)"
            subtitle="Register handed-in item and issue intake documentation"
          />
        </div>
      </PageHeader>

      <div className="space-y-6">
        {/* Sale Invoice Lookup & Item Picker Card */}
        <Card>
          <CardHeader className="py-3 px-4 border-b flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Receipt className="h-4 w-4 text-primary" />
              <span>{selectedSale ? "Linked Invoice & Items" : "Find Sale Invoice"}</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-normal">
                {useSaleLookup ? "Lookup from Sale" : "Manual Walk-in"}
              </span>
              <Switch
                id="flow-toggle"
                checked={useSaleLookup}
                onCheckedChange={(val) => {
                  setUseSaleLookup(val);
                  if (!val) {
                    handleClearSale();
                  }
                }}
                aria-label="Toggle sale lookup mode"
              />
            </div>
          </CardHeader>

          <CardContent className="p-4 space-y-4">
            {!useSaleLookup ? (
              <p className="text-xs text-muted-foreground py-1">
                Manual walk-in mode active. Claim fields below can be entered manually without linking to an existing invoice.
              </p>
            ) : (
              <>
                {/* Search bar is ONLY rendered when no sale is currently selected */}
                {!selectedSale && (
                  <div className="flex gap-2">
                    <div className="relative flex-1" ref={searchContainerRef}>
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        ref={searchInputRef}
                        className="pl-8 text-xs"
                        placeholder="Search by invoice number (e.g. INV-10023) or customer name/phone..."
                        value={saleSearchQuery}
                        onChange={(e) => {
                          setSaleSearchQuery(e.target.value);
                          setIsSearchDropdownOpen(true);
                        }}
                        onFocus={() => {
                          if (saleSearchQuery.trim()) {
                            setIsSearchDropdownOpen(true);
                          }
                        }}
                      />

                      {/* Floating Search Results Dropdown */}
                      {isSearchDropdownOpen && debouncedSaleQuery && (
                        <div className="absolute left-0 right-0 top-full mt-1.5 z-50 rounded-lg border bg-popover text-popover-foreground shadow-lg max-h-[260px] overflow-y-auto">
                          {isSearchingSales ? (
                            <div className="flex items-center justify-center p-4 text-xs text-muted-foreground gap-2">
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> Searching invoices...
                            </div>
                          ) : searchedSales.length > 0 ? (
                            <div className="p-1 space-y-1">
                              <div className="text-[11px] font-semibold text-muted-foreground px-2 py-1 uppercase tracking-wider">
                                Matching Invoices ({searchedSales.length})
                              </div>
                              {searchedSales.map((sale) => (
                                <div
                                  key={sale.id}
                                  onClick={() => handleSelectSale(sale)}
                                  className="p-2.5 rounded-md hover:bg-muted/70 cursor-pointer flex items-center justify-between border-b last:border-0 text-xs transition-colors"
                                >
                                  <div className="space-y-0.5">
                                    <span className="font-semibold text-foreground font-mono block">
                                      #{sale.invoice_number || sale.id}
                                    </span>
                                    <span className="text-muted-foreground text-[11px]">
                                      Customer:{" "}
                                      <strong className="text-foreground">
                                        {sale.customer?.customer_name ||
                                          sale.customer?.full_name ||
                                          sale.customer?.name ||
                                          "Walk-in"}
                                      </strong>{" "}
                                      • Date: {formatDate(sale.created_at)}
                                    </span>
                                  </div>
                                  <Badge variant="outline" className="shrink-0 text-[10px]">
                                    {Array.isArray(sale.items || sale.sale_items)
                                      ? `${(sale.items || sale.sale_items).length} items`
                                      : "Select Sale"}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="p-4 text-center text-xs text-muted-foreground">
                              No invoices found for &ldquo;{debouncedSaleQuery}&rdquo;
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {isSearchingSales && (
                      <div className="flex items-center px-2 text-xs text-muted-foreground shrink-0">
                        <Loader2 className="h-4 w-4 animate-spin mr-1" /> Searching...
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Sale & Item Picker Table */}
                {selectedSale && (
                  <div className="space-y-4">
                    {/* Neutral Invoice Details Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-muted/40 rounded-lg border text-xs">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-foreground text-sm font-mono">
                            Invoice #{selectedSale.invoice_number || selectedSale.id}
                          </span>
                          <span className="text-muted-foreground text-xs">•</span>
                          <span className="text-muted-foreground">
                            {formatDate(selectedSale.created_at)}
                          </span>
                        </div>
                        <div className="text-muted-foreground">
                          Customer:{" "}
                          <strong className="text-foreground font-medium">
                            {selectedSale.customer?.customer_name ||
                              selectedSale.customer?.full_name ||
                              selectedSale.customer?.name ||
                              "Walk-in Customer"}
                          </strong>
                          {selectedSale.store?.name && (
                            <> • Store: <span className="text-foreground">{selectedSale.store.name}</span></>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        type="button"
                        variant="outline"
                        className="h-8 text-xs font-medium gap-1.5 self-start sm:self-center"
                        onClick={handleClearSale}
                      >
                        <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                        Change Sale
                      </Button>
                    </div>

                    {/* Line items of the selected sale in clean ShadcnTable */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">
                          Select item to claim under warranty:
                        </span>
                        {selectedSaleItem && (
                          <span className="text-[11px] text-muted-foreground font-normal">
                            1 item selected
                          </span>
                        )}
                      </div>

                      <div className="rounded-md border overflow-x-auto">
                        <ShadcnTable>
                          <TableHeader>
                            <TableRow className="bg-muted/40 hover:bg-muted/40">
                              <TableHead className="w-10 text-center"></TableHead>
                              <TableHead className="min-w-[200px]">Product / Description</TableHead>
                              <TableHead className="min-w-[130px]">Serial Number</TableHead>
                              <TableHead className="min-w-[140px]">Warranty Coverage</TableHead>
                              <TableHead className="min-w-[110px]">Status</TableHead>
                              <TableHead className="text-right w-24">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {saleItems.length > 0 ? (
                              saleItems.map((item) => {
                                const retDetails = getSaleItemReturnDetails(item);
                                const itemSerials = getItemSerials(item);
                                const isSelected = selectedSaleItem?.id === item.id;
                                const isReturned = retDetails.isReturned;

                                return (
                                  <TableRow
                                    key={item.id}
                                    onClick={() => !isReturned && handleSelectSaleItem(item)}
                                    className={cn(
                                      "transition-colors text-xs",
                                      isReturned && "opacity-60 bg-muted/20 cursor-not-allowed",
                                      !isReturned && "cursor-pointer hover:bg-muted/50",
                                      isSelected && "bg-muted/60 font-medium"
                                    )}
                                  >
                                    <TableCell className="text-center">
                                      <input
                                        type="radio"
                                        name="selected_sale_item"
                                        checked={isSelected}
                                        disabled={isReturned}
                                        onChange={() => !isReturned && handleSelectSaleItem(item)}
                                        className="h-4 w-4 text-primary accent-primary cursor-pointer disabled:cursor-not-allowed"
                                        aria-label="Select item"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <div className="space-y-0.5">
                                        <div className="text-xs font-medium text-foreground">
                                          {item.product?.name || item.product_data?.name || item.name}
                                        </div>
                                        {item.product?.sku && (
                                          <div className="text-[11px] font-mono text-muted-foreground">
                                            SKU: {item.product.sku}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                      {itemSerials.length > 0 ? (
                                        itemSerials.join(", ")
                                      ) : (
                                        <span className="text-muted-foreground font-sans italic text-[11px]">N/A</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-xs">
                                      {item.warranty_end_date ? (
                                        <span>Till {formatDate(item.warranty_end_date)}</span>
                                      ) : (
                                        <span className="text-muted-foreground">Standard</span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {isReturned ? (
                                        <Badge
                                          variant="outline"
                                          className="text-[10px] h-5 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 font-medium gap-1"
                                        >
                                          <RotateCcw className="h-2.5 w-2.5 text-amber-600 dark:text-amber-400" />
                                          {retDetails.label || "Returned"}
                                        </Badge>
                                      ) : isSelected ? (
                                        <Badge
                                          variant="outline"
                                          className="text-[10px] h-5 border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-medium gap-1"
                                        >
                                          <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                                          Selected
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-[10px] h-5 text-muted-foreground font-normal">
                                          Eligible
                                        </Badge>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {isReturned ? (
                                        <span className="text-[11px] text-muted-foreground">Ineligible</span>
                                      ) : (
                                        <Button
                                          size="sm"
                                          type="button"
                                          variant={isSelected ? "default" : "outline"}
                                          className="h-7 text-xs px-2.5"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectSaleItem(item);
                                          }}
                                        >
                                          {isSelected ? "Selected" : "Select"}
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            ) : (
                              <TableRow>
                                <TableCell colSpan={6} className="text-center py-4 text-xs text-muted-foreground">
                                  No items found in this sale invoice.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </ShadcnTable>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Claim Form Fields */}
        <Card>
          <CardHeader className="py-3 px-4 border-b flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Package className="h-4 w-4 text-primary" />
              <span>Claim Intake Details</span>
            </CardTitle>
            {isItemAutoFilled && (
              <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                Auto-filled from invoice
              </Badge>
            )}
          </CardHeader>
          <CardContent className="p-4 pt-6 space-y-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Store */}
                <div className="space-y-2">
                  <Label>Store *</Label>
                  {isItemAutoFilled ? (
                    <Input
                      value={
                        stores.find((s) => s.id === formData.store_id)?.name ||
                        currentStore?.name ||
                        "Selected Store"
                      }
                      disabled
                      className="bg-muted/50 cursor-not-allowed"
                    />
                  ) : (
                    <ReactSelect
                      isClearable
                      onInputChange={(val) => setStoreSearchInput(val)}
                      placeholder="Select store..."
                      isLoading={isStoresFetching}
                      value={
                        formData.store_id
                          ? {
                            value: formData.store_id,
                            label:
                              stores.find((s) => s.id === formData.store_id)?.name ||
                              currentStore?.name ||
                              "Store",
                          }
                          : null
                      }
                      onChange={(opt) => handleSelectChange("store_id", opt ? opt.value : "")}
                      options={stores.map((s) => ({ value: s.id, label: s.name }))}
                    />
                  )}
                </div>

                {/* Customer */}
                <div className="space-y-2">
                  <Label>Customer *</Label>
                  {isItemAutoFilled || (formData.sale_id && formData.customer_name) ? (
                    <Input
                      value={formData.customer_name || "Customer"}
                      disabled
                      className="bg-muted/50 cursor-not-allowed"
                    />
                  ) : (
                    <ReactSelect
                      isClearable
                      onInputChange={(val) => setCustomerSearchInput(val)}
                      filterOption={() => true}
                      placeholder="Select customer..."
                      isLoading={isCustomersFetching}
                      value={
                        formData.customer_id
                          ? {
                            value: formData.customer_id,
                            label:
                              customers.find((c) => c.id === formData.customer_id)
                                ?.customer_name ||
                              customers.find((c) => c.id === formData.customer_id)?.name ||
                              formData.customer_name ||
                              "Customer",
                          }
                          : null
                      }
                      onChange={(opt) =>
                        handleSelectChange("customer_id", opt ? opt.value : "")
                      }
                      options={customers.map((c) => ({
                        value: c.id,
                        label: c.customer_name || c.full_name || c.name,
                      }))}
                    />
                  )}
                </div>

                {/* Product */}
                <div className="space-y-2">
                  <Label>Product *</Label>
                  {isItemAutoFilled ? (
                    <Input
                      value={formData.item_description || "Selected product"}
                      disabled
                      className="bg-muted/50 cursor-not-allowed"
                    />
                  ) : (
                    <ReactSelect
                      isClearable
                      onInputChange={(val) => setProductSearchInput(val)}
                      filterOption={() => true}
                      placeholder="Search product..."
                      isLoading={isProductsFetching}
                      value={
                        formData.product_id
                          ? {
                            value: formData.product_id,
                            label:
                              products.find((p) => p.id === formData.product_id)?.name ||
                              formData.item_description ||
                              "Product",
                          }
                          : null
                      }
                      onChange={handleProductSelect}
                      options={products.map((p) => ({
                        value: p.id,
                        label: `${p.name} ${p.sku ? `(${p.sku})` : ""}`,
                      }))}
                    />
                  )}
                </div>

                {/* Item Description */}
                <div className="space-y-2">
                  <Label htmlFor="item_description">Item Description *</Label>
                  <Input
                    id="item_description"
                    name="item_description"
                    value={formData.item_description}
                    onChange={handleInputChange}
                    placeholder="e.g. iPhone 15 Pro 128GB Black"
                    maxLength={255}
                    disabled={isItemAutoFilled}
                    className={isItemAutoFilled ? "bg-muted/50 cursor-not-allowed" : ""}
                    required
                  />
                </div>

                {/* Serial Number */}
                <div className="space-y-2">
                  <Label htmlFor="serial_number">Serial Number</Label>
                  <Input
                    id="serial_number"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleInputChange}
                    placeholder="e.g. SN-12345"
                    maxLength={255}
                    disabled={isItemAutoFilled}
                    className={isItemAutoFilled ? "bg-muted/50 cursor-not-allowed" : ""}
                  />
                </div>

                {/* Return Condition (defaults to defective) */}
                <div className="space-y-2">
                  <Label htmlFor="return_condition">Initial Return Condition</Label>
                  <Select
                    value={formData.return_condition}
                    onValueChange={(val) => handleSelectChange("return_condition", val)}
                  >
                    <SelectTrigger id="return_condition">
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      {WARRANTY_RETURN_CONDITIONS.map((cond) => (
                        <SelectItem key={cond.value} value={cond.value}>
                          {cond.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Warranty Start Date */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="warranty_start_date">Warranty Start Date</Label>
                    {productCoverage && (
                      <Badge
                        variant="outline"
                        className="text-[10px] h-4 font-normal text-muted-foreground"
                      >
                        Coverage: {productCoverage.label}
                      </Badge>
                    )}
                  </div>
                  <Input
                    id="warranty_start_date"
                    type="date"
                    name="warranty_start_date"
                    value={formData.warranty_start_date}
                    onChange={handleStartDateChange}
                    disabled={isItemAutoFilled}
                    className={isItemAutoFilled ? "bg-muted/50 cursor-not-allowed" : ""}
                  />
                  <span className="text-[11px] text-muted-foreground block">
                    {isItemAutoFilled
                      ? "Inherited from original sale."
                      : "Defaults from purchase/product. Changing updates end date."}
                  </span>
                </div>

                {/* Warranty End Date */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="warranty_end_date">Warranty End Date</Label>
                    {Boolean(formData.product_id) && !isItemAutoFilled && (
                      <button
                        type="button"
                        onClick={handleResetWarrantyDates}
                        className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                        title="Reset start and end dates to product default coverage"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Reset to product
                      </button>
                    )}
                  </div>
                  <Input
                    id="warranty_end_date"
                    type="date"
                    name="warranty_end_date"
                    value={formData.warranty_end_date}
                    onChange={handleEndDateChange}
                    disabled={isItemAutoFilled}
                    className={isItemAutoFilled ? "bg-muted/50 cursor-not-allowed" : ""}
                  />
                  <span className="text-[11px] text-muted-foreground block">
                    {isItemAutoFilled
                      ? "Inherited from original sale item."
                      : "Calculated from product. Editable anytime."}
                  </span>
                </div>
              </div>

              {/* Defect Description */}
              <div className="space-y-2">
                <Label htmlFor="defect_description">Defect Description *</Label>
                <Textarea
                  id="defect_description"
                  name="defect_description"
                  value={formData.defect_description}
                  onChange={handleInputChange}
                  placeholder="Describe the issue reported by the customer (e.g., Screen flickers intermittently, battery drops rapidly)..."
                  rows={4}
                  required
                />
              </div>

              {/* Form Bottom Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={createClaimMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createClaimMutation.isPending || activeReturnDetails?.isReturned || !canCreateClaim}
                >
                  {createClaimMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Registering Claim...
                    </>
                  ) : !canCreateClaim ? (
                    "Permission Required"
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Register Intake Claim
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
};

export default AddWarrantyClaim;
